import * as React from "react";
import { formatText } from "bernie-l10n";
import { QueryRoute } from "bernie-core";
import { inject, observer } from "mobx-react";
import LazyLoad from "react-lazyload";

import { UitkFigure, UitkImage, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkButton } from "uitk-react-button";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkIcon } from "uitk-react-icons";
import { UitkToolbar } from "uitk-react-toolbar";
import {
  UitkFullscreenDialog,
  UitkDialogContent,
  UitkDialogAnimationType
} from "uitk-react-dialog";
import {
  UitkMediaGalleryImageDialog,
  UitkMediaGalleryImageView,
  UitkMediaGalleryThumbnailView
} from "uitk-react-media-gallery";

import { GalleryMosaicThumbProps, GalleryMosaicProps, GalleryImage } from "../typings";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";
import { AttributionText } from "components/shared/AttributionText/AttributionText";
import { LazyLoaderPlaceHolder } from "components/utility/LazyLoaderPlaceholderWrapper";
import { getFullScreenImages } from "components/utility/GalleryImageUtil";

const GalleryMosaic = inject("routeStore")((props: GalleryMosaicProps) => {
  const { analytics, images, routeStore } = props;
  const galleryImages = getFullScreenImages(images);

  const [currentMediaGalleryIndex, setCurrentMediaGalleryIndex] = React.useState(0);

  const setDialogGalleryIndex = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery-nav.btn",
      action: Action.CLICK,
      linkName: "Navigating mosaic gallery viewer"
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    setCurrentMediaGalleryIndex(index);
  };

  const openMediaGallery = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event"
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    setCurrentMediaGalleryIndex(index);

    if (routeStore) {
      routeStore.updateSearch({ gallery: "open" });
    }
  };

  const openThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaicThumbnails",
      rfrr: "mosaic.gallery.thumbnails.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event"
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    if (routeStore) {
      routeStore.updateSearch({ thumbnails: "open" });
    }
  };

  const closeMediaGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event"
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    if (routeStore) {
      routeStore.closeDialog("gallery");
    }
  };

  const closeThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaicThumbnails",
      rfrr: "mosaic.gallery.thumbnails.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event"
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    if (routeStore) {
      routeStore.closeDialog("thumbnails");
    }
  };

  let thumbIndex = 0;
  const imageKeyHelper: ItemKeyHelper = new ItemKeyHelper("mosaic-image");

  const heroThumb = images &&
    images.length && (
      <DestinationGalleryMosaicThumb
        key={imageKeyHelper.next()}
        position={thumbIndex}
        image={images[0]}
        onImageSelected={openMediaGallery}
      />
    );

  let thumbs: JSX.Element[];
  if (images && images.length) {
    thumbs = images.slice(1, 5).map((image: GalleryImage) => {
      thumbIndex++;

      return (
        <DestinationGalleryMosaicThumb
          key={imageKeyHelper.next()}
          position={thumbIndex}
          image={image}
          onImageSelected={openMediaGallery}
        />
      );
    });
  } else {
    thumbs = [];
  }

  return (
    <div>
      <QueryRoute query={{ gallery: "open" }}>
        <div className="uitk-dialog-layer uitk-dialog-layer-responsive layer-overlay-active">
          <UitkMediaGalleryImageDialog
            animation={UitkDialogAnimationType.NONE}
            dialogShow={true}
            strictFullscreen={true}
            onDismiss={closeMediaGallery}
          >
            <UitkMediaGalleryImageView
              images={galleryImages}
              currentIndex={currentMediaGalleryIndex}
              onIndexChange={setDialogGalleryIndex}
            />
          </UitkMediaGalleryImageDialog>
        </div>
      </QueryRoute>

      <QueryRoute query={{ thumbnails: "open" }}>
        <div className="uitk-dialog-layer uitk-dialog-layer-responsive">
          <UitkFullscreenDialog
            strictFullscreen={false}
            dialogShow={false}
            animation={UitkDialogAnimationType.SLIDEUP}
            className="uitk-dialog-fullscreen-bg-default"
          >
            <UitkDialogContent className="no-padding">
              <UitkToolbar
                header={props.title}
                iconLabel={formatText("destinationThumbnailsGallery.button.close")}
                action={closeThumbnailsGallery}
              />

              <UitkMediaGalleryThumbnailView
                images={props.images}
                onImageSelected={openMediaGallery}
              />
            </UitkDialogContent>
          </UitkFullscreenDialog>
        </div>
      </QueryRoute>

      <div className={`GalleryMosaic GalleryMosaic--${thumbs.length + 1}`}>
        <div className="galleryMosaicHero">{heroThumb}</div>

        <div className="galleryMosaicThumbs" aria-hidden={true}>
          {thumbs}
        </div>

        <UitkScrim className="galleryMosaicScrim" type={UitkScrimType.BOTTOM}>
          <UitkButton onClick={openThumbnailsGallery} className="galleryMosaicButton">
            <span aria-hidden="true">{props.images.length}</span>
            <UitkIcon
              name="photo_library"
              title={formatText("galleryMosaic.seeAllTitle", props.images.length)}
              id="photo_library"
              shadow={true}
            />
          </UitkButton>
        </UitkScrim>
      </div>
    </div>
  );
});

export const DestinationGalleryMosaicThumb = observer((props: GalleryMosaicThumbProps) => {
  const { image } = props;

  const onImageSelected = () => {
    props.onImageSelected(props.position);
  };

  return (
    <div className="galleryMosaicThumb">
      <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
        <LazyLoad
          placeholder={
            <div onClick={onImageSelected}>
              <LazyLoaderPlaceHolder name={image.caption} />
            </div>
          }
          offset={250}
        >
          <div onClick={onImageSelected}>
            <UitkImage src={image.src} alt={image.alt} placeholderImage={true} />
          </div>
        </LazyLoad>
        {image.attribRequired &&
          image.source && (
            <AttributionText
              source={image.source}
              origin={image.origin}
              attribUrl={image.attribUrl}
              authorName={image.authorName}
              authorUrl={image.authorUrl}
              licenseSuite={image.licenseSuite}
              licenseName={image.licenseName}
              licenseUrl={image.licenseUrl}
            />
          )}
      </UitkFigure>
    </div>
  );
});

export default GalleryMosaic;
